import { motion } from "framer-motion";
import { Leaf, Zap, HeartHandshake } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const VALUES = [
    { icon: Leaf, title: "Stocked to your taste", text: "Chips, sodas, energy drinks, fresh deli sandwiches, protein bars — the mix is tailored to what your team actually reaches for." },
    { icon: Zap, title: "Smart by default", text: "Live telemetry tracks every item. Machines restock themselves before your team ever sees an empty coil." },
    { icon: HeartHandshake, title: "Local & accountable", text: "Owner-operated in Northern Virginia. You get a direct line to the person who services your machine." },
];

export default function About() {
    return (
        <section id="about" data-testid="about-section" className="relative py-24 sm:py-32 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-14 items-start">
                    <div className="lg:col-span-5">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, ease: EASE }}
                            className="font-mono-brand text-xs tracking-[0.25em] uppercase text-[#3FAE4A] mb-5"
                            data-testid="about-eyebrow"
                        >
                            The manifesto
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-[#0E1E12]"
                            data-testid="about-heading"
                        >
                            The breakroom is broken.{" "}
                            <span className="text-[#F58220]">We fix it for free.</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                            className="mt-6 text-base text-[#4A584C] leading-relaxed"
                            data-testid="about-body"
                        >
                            Most office vending is an afterthought — stale chips, flat soda, a machine
                            that eats dollars. HealthLogic was founded on a simple bet: when people
                            have genuinely good food within fifty steps of their desk, they work
                            better, feel better, and stay longer. We handle everything — machine,
                            install, stocking, service — and your office pays nothing.
                        </motion.p>

                        <div className="mt-10 space-y-6">
                            {VALUES.map((v, i) => (
                                <motion.div
                                    key={v.title}
                                    data-testid={`about-value-${i}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: EASE }}
                                    className="flex gap-4 group"
                                >
                                    <div className="shrink-0 h-11 w-11 rounded-2xl bg-[#0E1E12] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#3FAE4A]">
                                        <v.icon className="h-5 w-5 text-[#A6CE39]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-lg font-semibold text-[#0E1E12]">{v.title}</h3>
                                        <p className="text-sm text-[#4A584C] leading-relaxed mt-1">{v.text}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-7 relative">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease: EASE }}
                            className="relative spotlight-frame clip-notch"
                            data-testid="about-spotlight-frame"
                            onMouseMove={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                                e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                            }}
                        >
                            <img
                                src="https://images.unsplash.com/photo-1521986329282-0436c1f1e212?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwc25hY2tzfGVufDB8fHx8MTc4OTMyNTk4NHww&ixlib=rb-4.1.0&q=85&w=1200"
                                alt="Organic artisan healthy snacks assortment"
                                className="w-full h-[420px] sm:h-[540px] object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0E1E12]/60 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                                <div>
                                    <p className="font-mono-brand text-[11px] tracking-[0.25em] uppercase text-[#A6CE39] mb-2">
                                        Stocked weekly
                                    </p>
                                    <p className="font-display text-2xl font-bold text-white leading-tight">
                                        120+ rotating items,
                                        <br />
                                        tailored to your team
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30, rotate: -4 }}
                            whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                            className="absolute -bottom-8 -left-2 sm:-left-8 bg-white rounded-2xl border border-[#1B4D1B]/10 shadow-[0_24px_60px_-16px_rgba(14,30,18,0.25)] px-6 py-5"
                            data-testid="about-stat-card"
                        >
                            <p className="font-display text-3xl font-extrabold text-[#3FAE4A]">100%</p>
                            <p className="text-xs text-[#4A584C] uppercase tracking-[0.15em] mt-1">
                                free for qualifying offices
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
